/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2023, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react'
], (
	React
) => {

	const _getTotalOccupiedColumns = (cells) => {
		const colSpans = cells.reduce((accumulator, cell) => {
			const props = cell.props;
			if (props.colSpan > 0) {
				accumulator += props.colSpan - 1;
			}
			return accumulator;
		}, 0);
		return cells.length + colSpans;
	};

	const _createSpannedCell = ({ cellProp, fromRowIndex, rowIndex, renderRowHeaderCell }) => {
		let cell;
		let colSpan = 0;
		const { content, attributes } = renderRowHeaderCell({ rowIndex: cellProp.row, colIndex: cellProp.col });
		if (content) {
			const previousRowSpan = cellProp.rowSpan;
			if (previousRowSpan) {
				// update current cell rowSpan
				cellProp.rowSpan = rowIndex - fromRowIndex;
				// set rowSpan for the new cell
				attributes.rowSpan = previousRowSpan - cellProp.rowSpan;
			}
			cell = (
				<td {...attributes} key={cellProp.col}>
					{content}
				</td>
			);
			colSpan = cellProp.colSpan ? cellProp.colSpan : 1;
		}
		return { cell, colSpan };
	};

	const _createSpannedCells = ({ fromRow, fromRowIndex, rowIndex, rowCells, renderRowHeaderCell }) => {
		const newCells = [];
		const fromCells = fromRow.props.children;
		const fromNumOfColumns = _getTotalOccupiedColumns(fromCells);
		if (fromNumOfColumns > rowCells.length) {
			let cellIdx = 0;
			let numCellsAdded = 0;
			const numNewCells = fromNumOfColumns - _getTotalOccupiedColumns(rowCells);
			while ((cellIdx < fromCells.length) && (numCellsAdded < numNewCells)) {
				const cellProp = fromCells[cellIdx].props;
				const { cell, colSpan } = _createSpannedCell({ cellProp, fromRowIndex, rowIndex, renderRowHeaderCell });
				if (cell) {
					newCells.push(cell);
					numCellsAdded += colSpan;
				}
				++cellIdx;
			}
		}
		return newCells;
	};

	const _splitRowsBaseOnRowSpans = ({ rowIndex, rows, renderRowHeaderCell }) => {
		let splitRowsOccurred = true;
		if (rowIndex === 0) {
			// the set for rows to do not fit current table so move them to next table
			splitRowsOccurred = false;
			return { rowsForThisPage: [], rowsForNextPage: [...rows], splitRowsOccurred };
		}
		const firstRowCells = rows[0].props.children;
		const maxNumOfCells = _getTotalOccupiedColumns(firstRowCells);
		const currentRow = rows[rowIndex];
		const currentRowCells = currentRow.props.children;
		const hasRowSpans = currentRowCells.length < maxNumOfCells;
		if (hasRowSpans ) {
			// iterate backwards starting from rowIndex
			// if a row contains more cells than current row then add those cells to current row
			for (let idx = rowIndex - 1; idx >= 0; idx--) {
				const newCells = _createSpannedCells({ fromRow: rows[idx], fromRowIndex: idx, rowIndex, rowCells: currentRowCells, renderRowHeaderCell });
				currentRowCells.unshift(...newCells);
			}
		}
		const rowsForNextPage = rows.splice(rowIndex, rows.length - rowIndex);
		return { rowsForThisPage: [...rows], rowsForNextPage, splitRowsOccurred };
	};

	return class PrintScaleTableContext {
		constructor(options = {}) {
			this._columnHeadersHeight = options.columnHeadersHeight;
			this._maxTableHeight = options.maxTableHeight;
			this._renderRowHeaderCell = options.renderRowHeaderCell;
			this._renderTable = options.renderTable;
			this._rowHeights = options.rowHeights;
			this._titleHeight = options.titleHeight;
			this._totalHeight = this._titleHeight + this._columnHeadersHeight;
			this._groupCount = 0;
			this._pageBreaks = [];
			this._processedRowCount = 0;
			this._repeatingIterationStatus = {};
			this._rowIndex = -1;
			this._rows = [];
			this._rowsFromPreviousSpannedGroup = [];
			this._tables = [];
		}

		getPrintTables ({ rowsGroupedByRowSpans }) {
			for (this._groupCount = 0; this._groupCount < rowsGroupedByRowSpans.length; this._groupCount++) {
				this._rows = rowsGroupedByRowSpans[this._groupCount];
				this._splitRowGroup();
			}
			if (this._rowsFromPreviousSpannedGroup.length) {
				this._tables.push(this._renderTable({ tableRows: this._rowsFromPreviousSpannedGroup }));
			}
			return this._tables;
		}

		_splitRowGroup () {
			for(this._rowIndex = 0; this._rowIndex < this._rows.length; this._rowIndex++) {
				const rowHeight = this._rowHeights[this._rowIndex + this._processedRowCount];
				if ((this._totalHeight + rowHeight) <  this._maxTableHeight) {
					this._totalHeight += rowHeight;
					continue;
				}
				const done = this._splitRows();
				if (done) {
					break;
				}
			}
			if (this._rowIndex === this._rows.length) {
				this._rowsFromPreviousSpannedGroup.push(...this._rows);
				this._processedRowCount += this._rows.length;
			}
		}

		_splitRows () {
			let done = false;
			const { rowsForThisPage, rowsForNextPage, splitRowsOccurred } = _splitRowsBaseOnRowSpans({ rowIndex: this._rowIndex, rows: this._rows, renderRowHeaderCell: this._renderRowHeaderCell });
			if (splitRowsOccurred) {
				this._createTableForRowGroup({ rowsForThisPage, rowsForNextPage });
			} else if (this._repeatingIterationStatus[this._groupCount]) {
				this._createTableForPreviousRowGroup();
				done = true;
			} else {
				if (this._rowsFromPreviousSpannedGroup.length) {
					this._createTableForRemainingRows();
				}
				this._repeatingIterationStatus[this._groupCount] = true;
				this._rowIndex = -1;
			}
			return done;
		}

		_createTableForRowGroup ({ rowsForThisPage, rowsForNextPage }) {
			this._tables.push(this._renderTable({ tableRows: [...this._rowsFromPreviousSpannedGroup, ...rowsForThisPage] }));
			this._processedRowCount += rowsForThisPage.length;
			this._totalHeight = this._titleHeight + this._columnHeadersHeight;
			this._rowIndex = -1; //set to -1 to iterate over rows from beginning at index 0
			this._rows = [...rowsForNextPage];
			this._rowsFromPreviousSpannedGroup = [];
		}

		_createTableForPreviousRowGroup () {
			// Had already gone through break page once unsuccessfully so just dump these rows on a new page
			// Create a new page regardless whether rows can fit into one pdf page. This is to avoid inifinite looping
			// Should not get here with support page sizes
			this._tables.push(this._renderTable({ tableRows: this._rows }));
			this._processedRowCount += this._rows.length;
			this._totalHeight = this._titleHeight + this._columnHeadersHeight;
			this._rows = [];
		}

		_createTableForRemainingRows () {
			this._tables.push(this._renderTable({ tableRows: this._rowsFromPreviousSpannedGroup }));
			this._totalHeight = this._titleHeight + this._columnHeadersHeight;
			this._rowsFromPreviousSpannedGroup = [];
		}

		_renderTable ({ tableRows }) {
			if (tableRows.length) {
				const children = tableRows[0].props.children;
				if (children.length) {
					this._pageBreaks.push(children[0].props['aria-label']);
				} else {
					this._pageBreaks.push('Empty row');
				}
			}
			return this._renderTable({ tableRows, height: this._totalHeight });
		}
	};
});
